import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getEvent } from '@/services/api/events';
import { USER_ROUTES } from '@/routes';
import { useTranslation } from '@/hooks/useTranslation';
import { forgetActiveEvent, isEventUuid, readActiveEvent, rememberActiveEvent } from '@/utils/active-event';

/** Re-check ownership/existence before offering a remembered event destination. */
export function ActiveEventReturn({ userUuid }: { userUuid?: string }) {
  const { pathname, search } = useLocation();
  const { t } = useTranslation();
  const [resolved, setResolved] = useState<{ key: string; uuid: string; tab: string; name: string } | null>(null);
  const match = /^\/(ro|en)\/(pachete|setari|settings|conturi-conectate|connected-accounts|plati|payments)\/?$/.exec(pathname);
  const locale = match?.[1];
  const remembered = userUuid ? readActiveEvent(userUuid) : null;
  const requested = new URLSearchParams(search).get('event');
  const candidate = requested
    ? (isEventUuid(requested) ? { uuid: requested, tab: remembered?.uuid === requested ? remembered.tab : 'general' } : null)
    : remembered;
  const uuid = candidate?.uuid;
  const tab = candidate?.tab || 'general';
  const requestKey = `${userUuid}:${uuid}:${tab}`;

  useEffect(() => {
    let cancelled = false;
    if (!userUuid || !locale || !uuid) return;
    getEvent(uuid).then((event) => {
      if (cancelled || event.uuid !== uuid) return;
      rememberActiveEvent(userUuid, uuid, tab);
      setResolved({ key: requestKey, uuid, tab, name: event.name });
    }).catch((error: unknown) => {
      if (cancelled) return;
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403 || status === 404) forgetActiveEvent(userUuid, uuid);
      setResolved(null);
    });
    return () => { cancelled = true; };
  }, [userUuid, uuid, tab, locale, requestKey]);

  if (!locale || !resolved || resolved.key !== requestKey) return null;
  return (
    <div className="container mx-auto px-4 pb-3">
      <Link
        to={`${USER_ROUTES.event(locale, resolved.uuid)}?tab=${resolved.tab}`}
        className="inline-flex max-w-full items-center gap-2 rounded-md text-sm font-medium text-pink-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 dark:text-pink-300"
        data-testid="back-to-current-event"
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="break-words">{t('navigation.backToCurrentEvent', { name: resolved.name })}</span>
      </Link>
    </div>
  );
}
